import { useQuery, useQueryClient } from "@tanstack/solid-query";
import { createContext, createMemo, createSignal, useContext } from "solid-js";
import type { BuyCreditsModalModel } from "~/solidJs/entities/BuyCreditsModal";
import { PaymentMethodModel } from "~/solidJs/entities/PaymentMethod";
import { TeamMethods } from "~/solidJs/entities/Teams";
import { getPublicMessageFromError } from "~/solidJs/shared";
import { PaymentSystem } from "~/solidJs/shared/api/PaymentApi";
import { BuyCreditsMode, TeamApi, type TeamMember } from "~/solidJs/shared/api/TeamApi";
import {
	getIsBuyCreditsEnterpriseOpen,
	setIsBuyCreditsEnterpriseOpen
} from "~/solidJs/shared/models/globalStores";
import type { SelectClosedItem } from "~/solidJs/shared/ui/molecules/Select/SelectClosed";
import { showToastMessage, ToastMessageTypes } from "~/solidJs/widgets/WorkspaceToast";
import type { SelectedRecipient } from "./model";

// Type for context
type BuyCreditsEnterpriseContextType = ReturnType<typeof useBuyCreditsEnterprise>;

// Create context
export const BuyCreditsEnterpriseContext = createContext<BuyCreditsEnterpriseContextType>();

// Main hook for enterprise credits
export const useBuyCreditsEnterprise = () => {
	// Query client for cache invalidation - must be at the top with other hooks
	const queryClient = useQueryClient();

	// State signals
	const [selectedRecipient, setSelectedRecipient] = createSignal<SelectedRecipient | null>(null);
	const [showCreditsModal, setShowCreditsModal] = createSignal(false);
	const [selectedValue, setSelectedValue] =
		createSignal<SelectClosedItem<SelectedRecipient> | null>(null);

	// Query
	const teamInfoQuery = useQuery(() => TeamApi.getTeamInfoOptions());

	// Memoized computations
	const teamMembers = createMemo((): TeamMember[] => {
		const team = teamInfoQuery.data;
		if (!team?.members) return [];
		return team.members.filter((member) => TeamMethods.isMemberAccepted(member));
	});

	// Create recipient items list - only individual members (icon handled in UI)
	const recipientItems = createMemo((): SelectClosedItem<SelectedRecipient>[] => {
		return teamMembers().map((member) => ({
			id: member.user_id || String(member.company_account_id),
			label: member.user_name || `User ${member.user_id || member.company_account_id}`,
			value: member.user_id || String(member.company_account_id),
			subtitle: `Company Account ID: ${member.company_account_id}`
		}));
	});

	// Handle selection change
	const handleSelectionChange = (items: SelectClosedItem<SelectedRecipient>[]) => {
		const item = items[0];
		setSelectedValue(item || null);
	};

	// Handle continue button click
	const handleContinue = () => {
		const value = selectedValue();
		if (value) {
			handleRecipientSelect(value.value);
		}
	};

	const handleRecipientSelect = (recipient: SelectedRecipient) => {
		setSelectedRecipient(recipient);
		setShowCreditsModal(true);
	};

	const handleBackToSelection = () => {
		setShowCreditsModal(false);
		setSelectedRecipient(null);
		// Reset selection
		setSelectedValue(null);
	};

	const handleClose = () => {
		setShowCreditsModal(false);
		setSelectedRecipient(null);
		setSelectedValue(null);
		setIsBuyCreditsEnterpriseOpen(false);
	};

	// Get company_account_id from selected member - memoized
	const selectedCompanyAccountId = createMemo((): number | null => {
		const selected = selectedValue();
		if (!selected) return null;

		const members = teamMembers();
		const member = members.find((m) => m.user_id === selected.value);
		return member?.company_account_id ?? null;
	});

	// Get selected member name - memoized
	const selectedMemberName = createMemo((): string => {
		const selected = selectedValue();
		if (!selected) return "";
		const members = teamMembers();
		const member = members.find((m) => m.user_id === selected.value);
		return member?.user_name || `User ${selected.value}`;
	});

	const handleCustomPurchase = async (params: {
		recipient: SelectedRecipient;
		selectedPaymentMethod: PaymentMethodModel.PaymentMethod;
		currency: PaymentMethodModel.PaymentCurrency;
		tariff: BuyCreditsModalModel.CreditsPlan;
	}) => {
		const companyAccountId = selectedCompanyAccountId();
		if (!companyAccountId) {
			showToastMessage({
				title: "Error occurred",
				description: "No team member selected",
				type: ToastMessageTypes.Error
			});
			return;
		}

		try {
			let paymentSystem = params.selectedPaymentMethod.system;
			if (paymentSystem === "new") {
				paymentSystem =
					params.currency.value === "rub" ? PaymentSystem.YooKassa : PaymentSystem.Stripe;
			}

			// Determine payment_method_id:
			// - For "add-new", don't send payment_method_id (backend will create new payment method)
			// - For existing payment methods, send the actual ID
			const isAddingNewPaymentMethod =
				params.selectedPaymentMethod.id === PaymentMethodModel.ADD_NEW_PAYMENT_METHOD_ID;

			const response = await TeamApi.buyTeamCredits({
				product_api_label: params.tariff.id,
				mode: BuyCreditsMode.ForMember,
				company_account_id: companyAccountId,
				// Don't send payment_method_id for new payment methods
				...(isAddingNewPaymentMethod
					? {}
					: { payment_method_id: params.selectedPaymentMethod.id }),
				payment_system: paymentSystem
			});

			// Get recipient label BEFORE closing (handleClose resets selectedValue)
			const recipientLabel = selectedMemberName();

			// Handle redirect if needed (for payment systems like YooKassa or new payment method setup)
			if (response.redirect_url) {
				window.open(response.redirect_url, "_blank");
			}

			showToastMessage({
				title: `Credits purchase initiated for ${recipientLabel}`,
				description: response.message || `Plan: ${params.tariff.credits} credits`,
				type: ToastMessageTypes.Success
			});

			// Invalidate team info to refresh credits balance
			queryClient.invalidateQueries({ queryKey: ["teamInfo"] });

			handleClose();
		} catch (e) {
			showToastMessage({
				title: "Error occurred",
				description: getPublicMessageFromError(e) || "Failed to purchase credits",
				type: ToastMessageTypes.Error
			});
		}
	};

	const createCustomOnPurchase = (): BuyCreditsModalModel.CustomOnPurchaseHook => {
		return async (params) => {
			const recipient = selectedRecipient();
			if (!recipient) {
				throw new Error("No recipient selected");
			}
			await handleCustomPurchase({
				recipient,
				selectedPaymentMethod: params.selectedPaymentMethod,
				currency: params.currency,
				tariff: params.tariff
			});
		};
	};

	return {
		// State - expose signals directly (they are already functions)
		selectedRecipient,
		showCreditsModal,
		selectedValue,
		isOpen: getIsBuyCreditsEnterpriseOpen,

		// Computed
		teamMembers,
		teamInfoQuery,
		recipientItems,

		// Methods
		handleRecipientSelect,
		handleBackToSelection,
		handleClose,
		handleSelectionChange,
		handleContinue,
		createCustomOnPurchase
	};
};

// Global method to open BuyCreditsEnterprise modal
export const openBuyCreditsEnterprise = () => {
	setIsBuyCreditsEnterpriseOpen(true);
};

// Global method to close BuyCreditsEnterprise modal
export const closeBuyCreditsEnterprise = () => {
	setIsBuyCreditsEnterpriseOpen(false);
};

// Hook for using context
export const useBuyCreditsEnterpriseContext = () => {
	const context = useContext(BuyCreditsEnterpriseContext);
	if (!context) {
		throw new Error(
			"useBuyCreditsEnterpriseContext must be used within BuyCreditsEnterpriseProvider"
		);
	}
	return context;
};
